'use client';

import { c as _c } from "react/compiler-runtime";
import type { MouseEvent as ReactMouseEvent } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { type PartnerSocialLink, UserIdentity } from '@/components/profile/user-identity';
import { Link } from '@/i18n/navigation';
import { useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
export interface ProposalProposerCellProps {
  proposer: {
    walletAddress: string;
    displayName: string | null;
    handle: string | null;
    avatarUrl: string | null;
    avatarNsfw: boolean;
    avatarPreferences: {
      rasterise: boolean;
    } | null;
    connectedProviders: ReadonlyArray<{
      provider: string;
      username?: string | null | undefined;
      displayName?: string | null | undefined;
    }>;
  };
  /**
   * When true, render as a plain `<div>` instead of a `<Link>` — used when
   * this cell is itself nested inside a parent `<a>` (e.g. the `compact`
   * `ProposalFeedCard` variant rendered inside the similar-markets section,
   * whose root is already a card-level link). Avoids the invalid nested-`<a>`
   * hydration error.
   */
  disableNavigation?: boolean;
  /** Partner creator — shows the handshake mark beside the name. */
  partner?: boolean;
  partnerSocial?: PartnerSocialLink | null | undefined;
}
export function ProposalProposerCell(t0) {
  const $ = _c(41);
  const {
    proposer,
    disableNavigation: t1,
    partner: t2,
    partnerSocial: t3
  } = t0;
  const disableNavigation = t1 === undefined ? false : t1;
  const partner = t2 === undefined ? false : t2;
  const partnerSocial = t3 === undefined ? null : t3;
  const intent = useUserProfilePrefetchIntent(proposer.walletAddress);
  if (disableNavigation) {
    const t4 = proposer.avatarPreferences?.rasterise ?? false;
    let t5;
    if ($[0] !== proposer.avatarNsfw || $[1] !== proposer.avatarUrl || $[2] !== proposer.displayName || $[3] !== proposer.walletAddress || $[4] !== t4) {
      t5 = <UserAvatar size="xs" userId={proposer.walletAddress} displayName={proposer.displayName} avatarUrl={proposer.avatarUrl} nsfw={proposer.avatarNsfw} rasterise={t4} className="shrink-0" />;
      $[0] = proposer.avatarNsfw;
      $[1] = proposer.avatarUrl;
      $[2] = proposer.displayName;
      $[3] = proposer.walletAddress;
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== proposer.avatarNsfw || $[7] !== proposer.avatarUrl || $[8] !== proposer.connectedProviders || $[9] !== proposer.displayName || $[10] !== proposer.handle || $[11] !== proposer.walletAddress) {
      t6 = {
        userId: null,
        walletAddress: proposer.walletAddress,
        displayName: proposer.displayName,
        handle: proposer.handle,
        avatarUrl: proposer.avatarUrl,
        avatarNsfw: proposer.avatarNsfw,
        connectedProviders: proposer.connectedProviders
      };
      $[6] = proposer.avatarNsfw;
      $[7] = proposer.avatarUrl;
      $[8] = proposer.connectedProviders;
      $[9] = proposer.displayName;
      $[10] = proposer.handle;
      $[11] = proposer.walletAddress;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== partner || $[14] !== partnerSocial || $[15] !== t6) {
      t7 = <div className="flex w-full min-w-0 flex-col gap-0.5 overflow-hidden leading-tight" data-testid="proposal-proposer-cell-stack"><span data-testid="proposal-proposer-cell-handle" className="inline-flex w-full min-w-0 overflow-hidden"><UserIdentity user={t6} partner={partner} partnerSocial={partnerSocial} variant="stack" size="sm" className="max-w-full min-w-0 overflow-hidden" nameClassName="text-body font-medium" handleClassName="min-w-0 truncate text-caption" /></span></div>;
      $[13] = partner;
      $[14] = partnerSocial;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    let t8;
    if ($[17] !== t5 || $[18] !== t7) {
      t8 = <div className="flex w-full min-w-0 items-center gap-2">{t5}{t7}</div>;
      $[17] = t5;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    return t8;
  }
  const t4 = `/portfolio/${proposer.walletAddress}`;
  const t5 = proposer.avatarPreferences?.rasterise ?? false;
  let t6;
  if ($[20] !== proposer.avatarNsfw || $[21] !== proposer.avatarUrl || $[22] !== proposer.displayName || $[23] !== proposer.walletAddress || $[24] !== t5) {
    t6 = <UserAvatar size="xs" userId={proposer.walletAddress} displayName={proposer.displayName} avatarUrl={proposer.avatarUrl} nsfw={proposer.avatarNsfw} rasterise={t5} className="shrink-0" />;
    $[20] = proposer.avatarNsfw;
    $[21] = proposer.avatarUrl;
    $[22] = proposer.displayName;
    $[23] = proposer.walletAddress;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== proposer.avatarNsfw || $[27] !== proposer.avatarUrl || $[28] !== proposer.connectedProviders || $[29] !== proposer.displayName || $[30] !== proposer.handle || $[31] !== proposer.walletAddress) {
    t7 = {
      userId: null,
      walletAddress: proposer.walletAddress,
      displayName: proposer.displayName,
      handle: proposer.handle,
      avatarUrl: proposer.avatarUrl,
      avatarNsfw: proposer.avatarNsfw,
      connectedProviders: proposer.connectedProviders
    };
    $[26] = proposer.avatarNsfw;
    $[27] = proposer.avatarUrl;
    $[28] = proposer.connectedProviders;
    $[29] = proposer.displayName;
    $[30] = proposer.handle;
    $[31] = proposer.walletAddress;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== partner || $[34] !== t7) {
    t8 = <div className="flex w-full min-w-0 flex-col gap-0.5 overflow-hidden leading-tight" data-testid="proposal-proposer-cell-stack"><span data-testid="proposal-proposer-cell-handle" className="inline-flex w-full min-w-0 overflow-hidden"><UserIdentity user={t7} partner={partner} variant="stack" size="sm" className="max-w-full min-w-0 overflow-hidden" nameClassName="text-body font-medium" handleClassName="min-w-0 truncate text-caption" /></span></div>;
    $[33] = partner;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== intent || $[37] !== t4 || $[38] !== t6 || $[39] !== t8) {
    t9 = <Link href={t4} className="flex w-full min-w-0 items-center gap-2" onClick={_temp} {...intent}>{t6}{t8}</Link>;
    $[36] = intent;
    $[37] = t4;
    $[38] = t6;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
}
function _temp(e) {
  e.stopPropagation();
}